import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { createTrip, listTrips, type TripStatus } from "@/api/logistics";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { fmtDate, ruRub } from "@/lib/format";
import { cn } from "@/lib/utils";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const LS = { q: "wh_trip_q", status: "wh_trip_status" };
const lsGet = (k: string) => {
  try {
    return localStorage.getItem(k) ?? "";
  } catch {
    return "";
  }
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "planned", label: "В планах" },
  { value: "completed", label: "Завершён" },
  { value: "cancelled", label: "Отменён" },
];

const STATUS_LABEL: Record<TripStatus, string> = {
  planned: "В планах",
  completed: "Завершён",
  cancelled: "Отменён",
};

/** Список рейсов (логистика) — десктопная вёрстка: широкий контейнер + таблица. */
export function LogisticsListPage(): React.ReactElement {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rawQuery, setRawQuery] = useState(() => lsGet(LS.q));
  const query = useDebounced(rawQuery.trim(), 350);
  const [statusFilter, setStatusFilter] = useState<string>(() => lsGet(LS.status));

  useEffect(() => {
    try {
      localStorage.setItem(LS.q, rawQuery);
      localStorage.setItem(LS.status, statusFilter);
    } catch {
      /* приватный режим */
    }
  }, [rawQuery, statusFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ["trips", query, statusFilter],
    queryFn: ({ pageParam }) =>
      listTrips({ q: query, status: statusFilter, skip: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.reduce((n, p) => n + p.items.length, 0) : undefined,
  });

  const trips = data?.pages.flatMap((p) => p.items) ?? [];

  const createM = useMutation({
    mutationFn: () => createTrip({}),
    onSuccess: (trip) => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      navigate(`/logistics/trips/${trip.id}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось создать рейс")),
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Рейсы</h1>
        <Button onClick={() => createM.mutate()} disabled={createM.isPending}>
          {createM.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Новый рейс
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Номер, водитель, машина, перевозчик"
            className="pl-8"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Очистить"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value || "all"}
              type="button"
              onClick={() => setStatusFilter(o.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors",
                statusFilter === o.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background text-foreground hover:bg-accent",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {isError && (
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить рейсы: {(error as Error)?.message ?? "ошибка"}
        </Card>
      )}
      {!isLoading && !isError && trips.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          Рейсов пока нет — создайте первый.
        </p>
      )}

      {trips.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">№</th>
                  <th className="px-4 py-2.5 font-medium">Дата</th>
                  <th className="px-4 py-2.5 font-medium">Статус</th>
                  <th className="px-4 py-2.5 font-medium">Перевозчик</th>
                  <th className="px-4 py-2.5 font-medium">Водитель</th>
                  <th className="px-4 py-2.5 font-medium">Машина</th>
                  <th className="px-4 py-2.5 text-right font-medium">Точек</th>
                  <th className="px-4 py-2.5 text-right font-medium">Накл.</th>
                  <th className="px-4 py-2.5 text-right font-medium">Итого, ₽</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/logistics/trips/${t.id}`)}
                    className="cursor-pointer border-b last:border-0 hover:bg-accent"
                  >
                    <td className="px-4 py-2.5 font-semibold">
                      №{t.display_number ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                      {t.planned_date ? fmtDate(t.planned_date) : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="max-w-[16rem] truncate px-4 py-2.5">
                      {t.carrier_kontragent_name || "—"}
                    </td>
                    <td className="px-4 py-2.5">{t.driver_name || "—"}</td>
                    <td className="px-4 py-2.5">{t.vehicle_plate || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {t.stops_count}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {t.orders_count}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums">
                      {t.total_cost ? ruRub.format(t.total_cost) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div ref={sentinelRef} className="h-6" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: TripStatus }): React.ReactElement {
  const cls =
    status === "completed"
      ? "bg-emerald-100 text-emerald-700"
      : status === "cancelled"
        ? "bg-rose-100 text-rose-700"
        : "bg-amber-100 text-amber-700";
  return (
    <span className={cn("whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium", cls)}>
      {STATUS_LABEL[status]}
    </span>
  );
}
